import moment from 'moment';
import { StatusFilter, Filter } from '../filters/types';
import { IFilterState } from '../filters/reducer';
import { ICreativity, Status } from '../creativities/types';

export const statusFilterList: {
  key: StatusFilter;
  label: string;
  value: StatusFilter;
}[] = [
  { key: StatusFilter.ALL, label: 'All', value: StatusFilter.ALL },
  { key: StatusFilter.DRAFT, label: 'Draft', value: StatusFilter.DRAFT },
  ...(process.env.REACT_APP_PENDING_STATE
    ? [
        {
          key: StatusFilter.PENDING,
          label: 'Pending',
          value: StatusFilter.PENDING,
        },
      ]
    : []),
  {
    key: StatusFilter.SCHEDULED,
    label: 'Scheduled',
    value: StatusFilter.SCHEDULED,
  },
  { key: StatusFilter.ONGOING, label: 'On Going', value: StatusFilter.ONGOING },
  {
    key: StatusFilter.COMPLETED,
    label: 'Completed',
    value: StatusFilter.COMPLETED,
  },
];

export const groupFilterList = [];

const parseCreativeDate = (value?: string) => {
  if (!value) {
    return null;
  }

  const parsedDate = moment(
    value,
    [moment.ISO_8601, 'DD/MM/YYYY', 'D/M/YYYY', 'DD-MM-YYYY'],
    true
  );

  return parsedDate.isValid() ? parsedDate.startOf('day') : null;
};

const isPublished = ({ publishedAt, status }: ICreativity) =>
  !!publishedAt || status === Status.PUBLISHED;

const hasStarted = ({ startDate }: ICreativity) => {
  const date = parseCreativeDate(startDate);

  return !!date && date.isSameOrBefore(moment().startOf('day'));
};

const hasFinished = ({ endDate }: ICreativity) => {
  const date = parseCreativeDate(endDate);

  return !!date && moment().startOf('day').isAfter(date);
};

const statusFilterPredicates = {
  [StatusFilter.ALL]: () => true,
  [StatusFilter.ONGOING]: (creativity: ICreativity) =>
    isPublished(creativity) && hasStarted(creativity) && !hasFinished(creativity),
  [StatusFilter.SCHEDULED]: (creativity: ICreativity) =>
    isPublished(creativity) && !hasStarted(creativity) && !hasFinished(creativity),
  [StatusFilter.DRAFT]: (creativity: ICreativity) =>
    creativity.status === Status.DRAFT,
  [StatusFilter.PENDING]: (creativity: ICreativity) =>
    creativity.status === Status.PENDING_VALIDATION,
  [StatusFilter.COMPLETED]: (creativity: ICreativity) =>
    creativity.status === Status.COMPLETED,
};

export const filtersSelector = (type: Filter) => (state: {
  filters: IFilterState;
}) => {
  const filter = state.filters.filters[type];

  if (!filter) {
    return filterOps.default;
  }

  return filterOps[type][filter]
    ? filterOps[type][filter]()
    : filterOps[type].selection(filter);
};

const filterOps: {
  [key: string]: any;
} = {
  default: () => true,
  [Filter.GROUP]: {
    All: () => () => true,
    selection: (value: string) => (creativity: ICreativity) =>
      (creativity && creativity.groups && creativity.groups.includes(value)) ||
      creativity.allPlayers,
  },
  [Filter.STATUS]: {
    [StatusFilter.ALL]: () => statusFilterPredicates[StatusFilter.ALL],
    selection: (values: string[]) => (creativity: ICreativity) => {
      return (
        values &&
        values.length &&
        (values.includes(StatusFilter.ALL) ||
          values.some(value => {
            const predicate =
              statusFilterPredicates[
                value as keyof typeof statusFilterPredicates
              ];

            return predicate ? predicate(creativity) : false;
          }))
      );
    },
    [StatusFilter.ONGOING]: () => statusFilterPredicates[StatusFilter.ONGOING],
    [StatusFilter.SCHEDULED]: () =>
      statusFilterPredicates[StatusFilter.SCHEDULED],
    [StatusFilter.DRAFT]: () => statusFilterPredicates[StatusFilter.DRAFT],
    [StatusFilter.PENDING]: () => statusFilterPredicates[StatusFilter.PENDING],
    [StatusFilter.COMPLETED]: () =>
      statusFilterPredicates[StatusFilter.COMPLETED],
  },
};
